/**
 * LINK PREVIEWS (CONTEXT-DOCS.md §L, Slack-style) — under a message, one card per link it shared:
 *   page  — a left rule, the site, the title (opens the page), two lines of description, a thumbnail (OUR copy of the
 *           image — viewers never call the site); "read for context" when its text joined the room's context
 *   video — title · channel and a 16:9 thumbnail with ▶; the player (YouTube via youtube-nocookie) loads ONLY on click
 * Nothing shows while a link is being read or when it couldn't be (the link in the text still works). The author /
 * a moderator removes a preview (✕) — the link and its context stay.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, Play } from 'lucide-react';
import { getDiscussionLink, hideDiscussionLink } from '@stepflow/ui-shared/services/dataPlaneApi';

/** Which links get a card. Pure. */
export function visibleLinks(m) {
  return ((m && m.links) || []).filter((l) => l && !l.hidden && l.status === 'ready'
    && (l.kind === 'page' || l.kind === 'video') && (l.title || l.description));
}

function useImage(networkId, poolId, m, l, on) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let gone = false;
    if (!on || !l.hasImage || !networkId) return undefined;
    getDiscussionLink(networkId, m, l.index, { pool: poolId })
      .then((r) => { if (!gone) setUrl((r && r.url) || ''); })
      .catch(() => {});
    return () => { gone = true; };
  }, [networkId, poolId, m.id, l.index, l.hasImage, on]);   // eslint-disable-line react-hooks/exhaustive-deps
  return url;
}

function useSeen(ref) {
  const [seen, setSeen] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (seen || !ref.current || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) setSeen(true); }, { rootMargin: '200px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen]);
  return seen;
}

export function LinkCard({ networkId, poolId, message, link: l, canHide = false, onHidden = null }) {
  const ref = useRef(null);
  const seen = useSeen(ref);
  const img = useImage(networkId, poolId, message, l, seen);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const host = (() => { try { return new URL(l.url).hostname.replace(/^www\./, ''); } catch { return ''; } })();
  const hide = async () => {
    setBusy(true);
    try {
      const r = await hideDiscussionLink(networkId, message, l.index, { pool: poolId });
      if (onHidden && r && r.message) onHidden(r.message);
    } catch { setBusy(false); }
  };
  const close = canHide ? (
    <button type="button" data-testid={`link-hide-${message.id}-${l.index}`} aria-label="Remove preview" title="Remove preview"
      onClick={hide} disabled={busy}
      className="absolute right-1 top-1 hidden h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 group-hover/link:inline-flex">
      <X className="h-3.5 w-3.5" aria-hidden />
    </button>
  ) : null;
  const meta = (
    <div className="flex items-center gap-1.5 text-[0.75rem] text-gray-500">
      <span className="font-medium text-gray-700">{l.site || host}</span>
      {l.inContext ? <span title="Its text joined the room's context — agents can read it">· read for context</span> : null}
    </div>
  );
  if (l.kind === 'video') {
    const v = l.video || {};
    return (
      <div ref={ref} data-testid={`link-card-${message.id}-${l.index}`} data-kind="video"
        className="group/link relative mt-1.5 max-w-[30rem] border-l-4 border-gray-200 pl-3">
        {close}
        {meta}
        <a href={l.url} target="_blank" rel="noopener noreferrer" className="block text-[0.9375rem] font-bold text-blue-700 hover:underline">{l.title || 'Video'}</a>
        {l.description ? <div className="text-[0.8125rem] text-gray-600">{l.description}</div> : null}
        <div className="mt-1.5 aspect-video w-full overflow-hidden rounded-lg bg-gray-900">
          {playing && v.embed ? (
            <iframe data-testid={`link-player-${message.id}-${l.index}`} src={v.embed} title={l.title || 'Video'}
              className="h-full w-full" allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
          ) : (
            <button type="button" data-testid={`link-play-${message.id}-${l.index}`} onClick={() => setPlaying(true)}
              aria-label={`Play ${l.title || 'the video'}`} className="group/play relative h-full w-full">
              {img ? <img src={img} alt="" className="h-full w-full object-cover opacity-90 transition-opacity group-hover/play:opacity-100" /> : null}
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="inline-flex h-12 w-16 items-center justify-center rounded-xl bg-black/70 text-white transition-colors group-hover/play:bg-red-600">
                  <Play className="h-6 w-6 fill-current" aria-hidden />
                </span>
              </span>
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div ref={ref} data-testid={`link-card-${message.id}-${l.index}`} data-kind="page"
      className="group/link relative mt-1.5 flex max-w-[36rem] gap-3 border-l-4 border-gray-200 pl-3">
      {close}
      <div className="min-w-0 flex-1">
        {meta}
        <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`link-title-${message.id}-${l.index}`}
          className="block text-[0.9375rem] font-bold leading-snug text-blue-700 hover:underline">{l.title || l.url}</a>
        {l.description ? <div className="line-clamp-2 text-[0.8125rem] leading-snug text-gray-700">{l.description}</div> : null}
      </div>
      {img ? <img src={img} alt="" data-testid={`link-image-${message.id}-${l.index}`} className="h-20 w-20 shrink-0 rounded-md object-cover" /> : null}
    </div>
  );
}

export default function LinkPreviews({ networkId, poolId, message, canHide = false, onHidden = null }) {
  const links = visibleLinks(message);
  if (!links.length) return null;
  return (
    <div data-testid={`link-previews-${message.id}`}>
      {links.map((l) => <LinkCard key={l.index} networkId={networkId} poolId={poolId} message={message} link={l}
        canHide={canHide} onHidden={onHidden} />)}
    </div>
  );
}
